import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import { ArrowRight, Zap } from "lucide-react";
import { agentMap, debateScript, toneSurface } from "@/lib/mockData";
import { cn } from "@/lib/utils";

const TYPE_SPEED = 16;
const PAUSE_BETWEEN = 220;

export default function LiveDebatePreview() {
  const sectionRef = useRef(null);
  const inView = useInView(sectionRef, { once: true, amount: 0.35 });
  const [messages, setMessages] = useState([]);
  const [done, setDone] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!inView) return;
    const timers = [];
    let cumulative = 0;
    debateScript.forEach((line) => {
      const start = setTimeout(() => {
        setMessages((prev) => [
          ...prev,
          { agentId: line.agentId, fullText: line.text, displayed: "" },
        ]);
        for (let i = 0; i < line.text.length; i++) {
          const t = setTimeout(
            () => {
              setMessages((prev) => {
                const next = [...prev];
                const last = next[next.length - 1];
                if (!last) return prev;
                next[next.length - 1] = {
                  ...last,
                  displayed: line.text.slice(0, i + 1),
                };
                return next;
              });
            },
            (i + 1) * TYPE_SPEED,
          );
          timers.push(t);
        }
      }, cumulative);
      timers.push(start);
      cumulative += line.text.length * TYPE_SPEED + PAUSE_BETWEEN;
    });
    const endT = setTimeout(() => setDone(true), cumulative + 200);
    timers.push(endT);
    return () => timers.forEach(clearTimeout);
  }, [inView]);

  // Auto-scroll
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
  }, [messages]);

  return (
    <section
      ref={sectionRef}
      data-testid="live-debate-preview"
      id="boardroom"
      className="px-5 py-24 md:px-8 md:py-32"
    >
      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 lg:grid-cols-2">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.5 }}
        >
          <div className="eyebrow">Inside the boardroom</div>
          <h2 className="mt-3 text-[40px] font-black leading-[1.02] tracking-tight text-zinc-900 md:text-[60px]">
            Watch eight minds <br />
            argue your next move.
          </h2>
          <p className="mt-5 max-w-lg text-[15px] leading-relaxed text-zinc-600 md:text-[16px]">
            When you set a goal, the boardroom spins up a parallel debate.
            Specialists challenge each other in real time and converge on a
            decision — with reasoning, confidence, and risk fully visible.
          </p>
          <Link
            to="/boardroom"
            data-testid="live-debate-cta"
            className="group mt-7 inline-flex items-center gap-1.5 text-[14px] font-semibold text-zinc-900 hover:text-black"
          >
            See full boardroom
            <ArrowRight
              className="h-4 w-4 transition-transform group-hover:translate-x-0.5"
              strokeWidth={2.4}
            />
          </Link>
        </motion.div>

        {/* Right: chat window */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="card-soft rounded-[28px] p-5"
        >
          <div className="mb-3 flex items-center justify-between">
            <div>
              <div className="eyebrow">Debate feed</div>
              <div className="mt-1 text-[14px] font-bold text-zinc-900">
                Goal · Pivot to Enterprise SaaS
              </div>
            </div>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-violet-50 px-2 py-1 text-[10px] font-bold uppercase tracking-[0.14em] text-violet-700">
              <span className="h-1.5 w-1.5 rounded-full bg-violet-600 idea-dot-pulse" />
              {done ? "Decided" : "Streaming"}
            </span>
          </div>
          <div
            ref={containerRef}
            className="idea-scroll min-h-[300px] max-h-[420px] space-y-3 overflow-y-auto pr-1"
          >
            {messages.map((m, i) => {
              const agent = agentMap[m.agentId];
              const tone = toneSurface[agent?.tone] ?? toneSurface.neutral;
              const isDark = agent?.tone === "dark";
              const streaming = m.displayed.length < m.fullText.length;
              return (
                <div
                  key={i}
                  data-testid={`landing-debate-msg-${i}`}
                  className="flex items-start gap-3"
                >
                  <div
                    className={cn(
                      "flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-[11px] font-extrabold",
                      isDark
                        ? "bg-zinc-900 text-white"
                        : "border border-black/[0.04] bg-white text-zinc-800",
                    )}
                  >
                    {agent?.name?.[0]}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="mb-1 flex items-center gap-2">
                      <span className="text-[12.5px] font-bold text-zinc-900">
                        {agent?.name}
                      </span>
                      <span className="text-[10px] uppercase tracking-[0.14em] text-zinc-400">
                        {agent?.role}
                      </span>
                    </div>
                    <div
                      className={cn(
                        "rounded-2xl px-3.5 py-2.5 text-[12.5px] leading-relaxed",
                        tone.bg,
                        tone.quote,
                        isDark ? "shadow-md" : "border border-black/[0.04]",
                      )}
                    >
                      <span className={streaming ? "idea-caret" : undefined}>
                        {m.displayed}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {done && (
            <motion.div
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              data-testid="landing-debate-decision-chip"
              className="mt-4 inline-flex items-center gap-2 rounded-full bg-black px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.14em] text-white"
            >
              <Zap className="h-3 w-3" strokeWidth={2.6} />
              Decision · Pivot to Enterprise SaaS · 86% confidence
            </motion.div>
          )}
        </motion.div>
      </div>
    </section>
  );
}
